<template>
    <div class="right-content">
      <h2 class="payment-title">{{ $t('message.checkout.payWithCard') }}</h2>
      <!-- 国家下拉框 -->
      <div class="country-select-wrapper" v-if="showCountrySelect !== false">
        <div class="country-select-label">{{ $t('message.checkout.country') }}</div>
        <CountrySelect
          style="height: 38px;"
          :disabled="submitLoading"
          v-model="order.paymentCountry"
          @change="handleCountryChange"
        />
      </div>
        <div class="loading-placeholder" v-loading="formLoading">
          <div class="payment-form-content">
              <!-- 这里放支付表单内容 -->
              <dynamic-form
                ref="dynamicFormRefPayment"
                :config="getPaymentConfig"
                :validation-mode="'format-only'"
                :show-submit-button="false"
                :initial-values="initialValues"
                :extra-props="{ cardTypeInfo: cardTypeFilter(order?.cardTypeInfo) }"
                @field-focus="handlePaymentFieldFocus"
                @field-blur="handlePaymentFieldBlur"
                @field-change="handlePaymentFieldChange"
                @card-valid="handleCardValid"
              />

              <!-- 独立的分期选择器 -->
              <div v-if="order?.canInstallment" class="installment-field">
                <div class="installment-label">{{ $t('message.checkout.intallment') }}</div>
                <el-select
                  v-model="currentPeriodValue"
                  :disabled="instalmentDisabled"
                  :placeholder="$t('message.checkout.intallment')"
                  popper-class="loading-select"
                  @focus="handleInstallmentFocus"
                  filterable
                  autocomplete="off"
                  auto-complete="off"
                  @visible-change="handleInstallmentVisibleChange"
                  @change="handleInstallmentChange"
                  class="installment-select">
                  <!-- 正常选项 -->
                  <template v-if="!instalmentLoading && installmentOptions.length > 0">
                    <el-option
                      v-for="(option, index) in installmentOptions"
                      :key="index"
                      :value="option.value"
                      :label="option.label">
                    </el-option>
                  </template>
                  <!-- 加载状态 -->
                  <el-option
                    v-if="instalmentLoading"
                    key="loading"
                    disabled
                    value="loading"
                    class="select-loading-item">
                    <span style="display: flex; align-items: center; justify-content: center; width: 100%; ">
                    <div class="installment-loading" v-loading="instalmentLoading" element-loading-background="transparent"></div>
                    </span>
                  </el-option>
                </el-select>
                <!-- 分期提示 -->
                <p v-if="!['', '1'].includes(currentPeriodValue)" class="installment-tip">
                  {{ $t('message.checkout.intallmentTip') }}
                </p>
              </div>
          </div>
          <!-- Billing address/Contact information -->
          <div v-if="!isEmpty(getBillingConfig)">
            <h2 class="billing-title" @click="showBillingForm = !showBillingForm">
              {{ $t('message.checkout.billingAddress')}}
              <img src="./arror.png" :class="{ 'arrow-open': !showBillingForm }"
                  class="arrow-icon" />
            </h2>
            <transition name="collapse">
              <div class="payment-form-content" v-show="showBillingForm">
                  <!-- 这里放账单表单内容 -->
                  <dynamic-form
                    ref="dynamicFormRefBilling"
                    :config="getBillingConfig"
                    :validation-mode="'format-only'"
                    :show-submit-button="false"
                    @field-focus="handleBillingFieldFocus"
                    @field-change="handleBillingFieldChange"
                  />
              </div>
            </transition>
                      </div>

          <on-button type="submit" :loading="submitLoading" class="pay-btn" @click.prevent="validateAndSubmit">{{ submitBtnLabel }} </on-button>
          <div class="pay-agreement">
              <span>{{ $t('message.checkout.policy.prefix') }}</span>
              <a href="#" class="pay-link" @click.prevent="viewPolicy">{{ $t('message.checkout.privacy') }}</a>
              <span>{{ $t('message.checkout.policy.suffix') }}</span>
          </div>
      <div v-if="order && order.subscription">
        <p class="subsciption" v-if='order && order.subscription.trialDays'>
          *{{ $t('message.checkout.subscriptionNo1', {
            num:
              formatMoneyWithCommas(order.txnAmount) + ' ' + order.txnCurrency
          }) }} {{ formatFrequencyLabel(order.subscription.frequencyType, order.subscription.frequencyPoint) }}{{ this.$i18n.locale === 'zh-cn' ? '。' : '.' }}
          <!-- 中文显示。  非中文显示. -->
          <br>
          {{ $t('message.checkout.subscriptionNo3') }}

        </p>
        <p class="subsciption" v-else>
          *{{ $t('message.checkout.subscriptionNo') }}
        </p>
      </div>
          <div class="pay-return" v-if="showCancelBtn && (order.cancelUrl || order.returnUrl)">
              <img src="./back.png" class="return-icon" />
              <a href="#" class="return-link" @click.prevent="onCancel">{{ $t('message.checkout.returnMerchant') }}</a>
          </div>
        </div>
        <Privacy :open="showPolicy" @on-close="showPolicy = false"></Privacy>
    </div>
</template>

<script>
import { getInstallmentInfo } from '@/api/checkout';
import Privacy from '@/components/privacy/index.vue';
import OnButton from '@/componentsV2/base/button/index.vue';
import { luhn } from '@/componentsV2/busi/card-information-form/rules.js';
import { DynamicForm } from '@/componentsV2/busi/dynamic-form';
import { getFieldConfig } from '@/componentsV2/busi/dynamic-form/filed-manager';
import { deviceFingerprintManager as sendEventInstance } from '@/plugins/eventTrackManager/deviceFingerprintManager';
import { formatFrequencyLabel, formatMoneyWithCommas } from '@/utils/tools/format';

export default {
  name: 'PaymentForm',
  components: {
    DynamicForm,
    OnButton,
    Privacy,
  },
  props: {
    order: {
      type: Object,
      default: () => {},
    },
    formLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // 默认折叠
      showBillingForm: false,
      showPolicy: false,
      // 表单初始值
      initialValues: {},
      // 分期选项缓存
      installmentOptions: [],
      // 分期字段是否禁用
      installmentDisabled: true,
      // 上次验证通过的卡号
      lastValidCardNum: '',
      // 提交按钮加载状态
      submitLoading: false,
      // 当前分期值
      currentPeriodValue: '',
      // 是否重新请求分期选项
      instalmentRefresh: false,
      // 添加与PC版一致的数据属性
      instalmentLoading: false, // 与PC版命名一致
      instalmentDisabled: true, // 与PC版命名一致
      instalmentInfo: null, // 存储完整的分期信息
    };
  },
  computed: {
    getPaymentConfig() {
      // 白名单，这些都属于本地支付，不在标准收银台显示
      const whiteList = ['issueDate', 'bankName', 'blikcode', 'walletAccountName'];
      const apiFields = this.order?.paymentConfigurationFields?.filter(field => !whiteList.includes(field.name)) || [];

      // 深拷贝一份字段配置
      const paymentFields = [...apiFields];

      // 卡信息回填和字段隐藏逻辑
      if (this.order?.cardInfo) {
        // 处理持卡人姓名字段
        const holderNameField = paymentFields.find(field => field.name === 'cardHolderName');
        if (holderNameField && this.order.cardInfo.holderName) {
          const holderNameArr = this.order.cardInfo.holderName?.trim().split(' ');
          if (holderNameArr.length > 1) {
            holderNameField.display = 'F'; // 隐藏字段
            holderNameField.value = this.order.cardInfo.holderName?.trim(); // 设置默认值
          }
        }

        // 处理卡号字段
        const cardNumField = paymentFields.find(field => field.name === 'cardNum');
        if (cardNumField && this.order.cardInfo.cardNumber) {
          cardNumField.value = this.order.cardInfo.cardNumber.replace(/\s*/g, '').replace(/(.{4})/g, '$1 ').trim();
        }

        // 处理CVV字段
        const cvvField = paymentFields.find(field => field.name === 'cvv');
        if (cvvField && this.order.cardInfo.cvv) {
          cvvField.value = this.order.cardInfo.cvv;
        }

        // 处理有效期字段
        const dateField = paymentFields.find(field => field.name === 'effectiveDate');
        if (dateField && this.order.cardInfo.month && this.order.cardInfo.year) {
          const year = this.order.cardInfo.year;
          dateField.value = `${this.order.cardInfo.month} / ${year.substring(year.length - 2, year.length)}`;
        }
      }

      // 注释：分期字段现在独立处理，不再添加到动态表单配置中

      return getFieldConfig({
        apiFields: paymentFields,
        fieldType: 'payment',
        country: this.order?.paymentCountry,
      });
    },
    getBillingConfig() {
      return getFieldConfig({
        apiFields: this.order?.billConfigurationFields,
        fieldType: 'billing',
        country: this.order?.paymentCountry,
      });
    },
    showCancelBtn() {
      return this.order?.brandConfig?.viewCancelBtn;
    },
    showCountrySelect() {
      return this.order?.brandConfig?.viewCountryBtn;
    },
    // 提交按钮文案
    submitBtnLabel() {
      // 如果选择了分期且不是全额付款，只显示 "Pay"
      if (this.currentPeriodValue && !['', '1'].includes(this.currentPeriodValue)) {
        return this.$t('message.checkout.pay');
      }

      if (this.order?.subscription?.trialDays) {
        return this.$t('message.checkout.startTrial');
      } else if (this.order?.subProductType === 'SUBSCRIBE') {
        return this.$t('message.checkout.subscribe') + ' - ' + formatMoneyWithCommas(this.order.txnAmount) + ' ' + this.order.txnCurrency;
      } else {
        return this.$t('message.checkout.pay') + ' ' + formatMoneyWithCommas(this.order.txnAmount) + ' ' + this.order.txnCurrency;
      }
    },
    // 兼容性计算属性
    installmentLoading() {
      return this.instalmentLoading;
    },
  },
  methods: {
    formatFrequencyLabel,
    formatMoneyWithCommas,
    sendBehaviorEvent(params) {
      const param = {
        m: this.order?.merchantTxnId || '',
        kd: this.order?.merchantNo || '',
        re: this.order?.retry || '',
        k: this.$route.query?.key || '',
        ct: 'CHECKOUT',
        co: this.order?.txnCurrency || '',
        at: this.order?.txnAmount || '',
        gwq: this.order?.txnId || '',
        bh: this.order?.paymentCountry || '',
        ...params,
      };
      sendEventInstance.send(param);
    },
    // 判断对象是否为空
    isEmpty(obj) {
      return Object.keys(obj).length === 0;
    },
    setSubmitLoading(loading) {
      this.submitLoading = loading;
    },
    validateAndSubmit() {
      this.sendBehaviorEvent({ e: 'click_payment_button' });
      // 设置验证模式为完整验证
      const paymentForm = this.$refs.dynamicFormRefPayment;
      const billingForm = this.$refs.dynamicFormRefBilling;

      // 设置存在的表单为完整验证模式
      if (paymentForm) {
        paymentForm.setValidationMode('full');
      }
      if (billingForm) {
        billingForm.setValidationMode('full');
      }

      this.$nextTick(async () => {
        try {
          // 创建验证任务数组
          const validationTasks = [];

          // 如果支付表单存在，添加到验证任务
          if (paymentForm) {
            validationTasks.push(paymentForm.handleSubmit().then(result => ({ form: 'payment', ref: paymentForm, result })));
          }

          // 如果账单表单存在，添加到验证任务
          if (billingForm) {
            validationTasks.push(billingForm.handleSubmit().then(result => ({ form: 'billing', ref: billingForm, result })));
          }

          // 并行验证所有表单
          const results = await Promise.all(validationTasks);

          // 检查所有表单是否都通过验证
          const allValid = results.every(item => item.result.valid);

          if (allValid) {
            // 收集所有表单数据
            const formData = {};
            if (paymentForm) {
              Object.assign(formData, paymentForm.getFormData());
            }
            if (billingForm) {
              Object.assign(formData, billingForm.getFormData());
            }

            // 添加分期值
            if (this.order?.canInstallment && this.currentPeriodValue) {
              formData.periodValue = this.currentPeriodValue;
            }

            // 提交数据
            this.$emit('submit', formData);
          } else {
            // 处理校验失败情况
            // 查找支付表单的验证结果
            const paymentResult = results.find(item => item.form === 'payment');

            // 如果支付表单存在且验证失败，优先显示支付表单错误
            if (paymentResult && !paymentResult.result.valid) {
              await paymentResult.ref.scrollToFirstError();
            } else {
              // 否则查找账单表单的验证结果
              const billingResult = results.find(item => item.form === 'billing');

              // 如果账单表单存在且验证失败
              if (billingResult && !billingResult.result.valid) {
                // 确保账单表单展开
                if (!this.showBillingForm) {
                  this.showBillingForm = true;
                  // 等待折叠动画完成
                  await new Promise(resolve => setTimeout(resolve, 300));
                }

                await billingResult.ref.scrollToFirstError();
              }
            }
          }
        } catch (error) {
        }
      });
    },
    // 初始化表单数据
    initFormData() {
      // 方法预留，未来可能需要添加初始化逻辑
    },
    handleCountryChange(country) {
      this.sendBehaviorEvent({ e: 'select_country' });
      const cardNum = this.$refs.dynamicFormRefPayment.getFormData()?.cardNum?.replace(/\s/g, '');

      // 国家变更时，重置分期状态
      if (this.order?.canInstallment) {
        this.installmentOptions = [];
        this.instalmentInfo = null;
        this.currentPeriodValue = '';
        this.lastValidCardNum = ''; // 重置卡号缓存，确保国家变更后重新请求

        // 检查当前卡号是否有效，如果有效则保持启用状态
        if (cardNum && cardNum.length > 10 && luhn(cardNum)) {
          this.instalmentDisabled = false; // 如果有有效卡号，保持启用
          this.instalmentRefresh = true; // 标记需要重新请求分期选项
        } else {
          this.instalmentDisabled = true; // 如果没有有效卡号，则禁用
        }
      }

      this.$emit('country-change', {
        country,
        cardNum,
      });
    },
    // 处理字段值变更
    handlePaymentFieldChange(field) {
      this.$emit('field-change', field);
      // 分期字段现在独立处理，不在这里处理
      this.sendBehaviorEvent({ e: 'input_payment_info' });
      if (field.name === 'cardNum') {
        this.sendBehaviorEvent({ e: 'fill_cardnumber' });
      }
    },
    handlePaymentFieldFocus(field) {
      // 处理字段聚焦事件（分期字段现在独立处理）
      this.sendBehaviorEvent({ e: 'focus_payment_info' });
    },
    handleBillingFieldFocus(field) {
      this.sendBehaviorEvent({ e: 'focus_billing_info' });
    },
    handleBillingFieldChange(data) {
      this.sendBehaviorEvent({ e: 'input_billing_info' });
    },
    handlePaymentFieldBlur(data) {
      // 无论哪个字段失焦，都获取所有字段的当前值
      const formData = this.$refs.dynamicFormRefPayment.getFormData();
      const cardNum = formData?.cardNum?.replace(/\s/g, '');
      const cvv = formData?.cvv;
      const cardHolderName = formData?.cardHolderName;
      const effectiveDate = formData?.effectiveDate;

      // 无论哪个字段失焦，都更新所有初始值
      this.initialValues = {
        ...this.initialValues,
        cardNum,
        cvv,
        cardHolderName,
        effectiveDate,
      };

      // 卡号特殊处理（保留原有逻辑）
      if (data.name === 'cardNum' && luhn(cardNum)) {
        this.$emit('cardNum-validate', cardNum);
      }
    },
    // 处理卡号验证通过事件
    handleCardValid(cardNum) {
      if (!cardNum || !this.order?.canInstallment) return;

      if (cardNum.length > 10) {
        this.instalmentDisabled = false; // 使用新的命名

        // 如果是新的卡号，重置分期选项并标记需要刷新
        if (this.lastValidCardNum !== cardNum) {
          // 重置分期选项和选择值
          this.installmentOptions = [];
          this.instalmentInfo = null;
          this.currentPeriodValue = '';
          this.instalmentRefresh = true; // 标记需要刷新，但不立即请求
        }
      } else {
        this.instalmentDisabled = true; // 确保卡号不满足条件时保持禁用
        // 清空分期选项和选择值
        this.installmentOptions = [];
        this.instalmentInfo = null;
        this.currentPeriodValue = '';
        this.instalmentRefresh = false; // 卡号不满足条件时重置刷新标记
      }

      // 添加卡号缓存，避免重复请求
      if (this.lastValidCardNum === cardNum) return;
      this.lastValidCardNum = cardNum;
    },

    // 卡种去重
    cardTypeFilter(cards) {
      if (!cards || !cards.length) return [];

      const supportCardTypes = [];
      const supportCardTypeNames = [];

      cards.forEach((item) => {
        // [VISA, MASTERCARD]卡种去重
        if (['VISA COMMERCIAL', 'MASTERCARD COMMERCIAL'].indexOf(item.cardType) < 0 ||
          (item.cardType === 'VISA COMMERCIAL' && supportCardTypeNames.indexOf('VISA') < 0) ||
          (item.cardType === 'MASTERCARD COMMERCIAL' && supportCardTypeNames.indexOf('MASTERCARD') < 0)) {
          supportCardTypeNames.push(item.cardType);
          supportCardTypes.push(item);
        }
      });
      return supportCardTypes;
    },

    // 新增：处理分期下拉框聚焦事件
    handleInstallmentFocus() {
      if (this.instalmentRefresh) {
        this.getInstalmentList();
      }
    },

    // 新增：处理分期下拉框可见性变化
    handleInstallmentVisibleChange(visible) {
      // 可以添加埋点或其他逻辑
      if (visible) {
        this.sendRiskDeviceFingerPrint && this.sendRiskDeviceFingerPrint({ e: 'click_installment_select' });
      }
    },

    // 新增：处理分期选择变化
    handleInstallmentChange(value) {
      this.currentPeriodValue = value;
      this.$emit('installment-change', value);
      this.sendBillingInfoInputEvent && this.sendBillingInfoInputEvent('periodValue'); // 与PC版保持一致的事件名
    },

    // 新增：获取分期列表（复制PC版逻辑）
    getInstalmentList() {
      const cardNum = this.$refs.dynamicFormRefPayment.getFormData()?.cardNum?.replace(/\s/g, '');

      if (!cardNum || !this.order?.canInstallment) return;

      // 设置加载状态
      this.instalmentLoading = true;

      const params = {
        appId: this.order.appId,
        merchantNo: this.order.merchantNo,
        amount: this.order.txnAmount,
        currency: this.order.txnCurrency,
        cardInfo: {
          cardNumber: cardNum,
        },
        country: this.order.paymentCountry,
      };

      // 调用API获取分期选项
      getInstallmentInfo(params)
        .then(res => {
          if (res.respCode !== '20000' || !res.data || !res.data.stages) {
            this.instalmentInfo = null;
            this.installmentOptions = [];
            return;
          }

          // 存储完整的分期信息
          this.instalmentInfo = res.data;

          // 转换选项格式 - 与PC版完全一致，确保value为字符串类型
          this.installmentOptions = res.data.stages.map(stage => {
            if (stage.stageNumber === '1') {
              return {
                label: this.$t('message.checkout.payInFull'),
                value: String(stage.stageNumber), // 确保为字符串
              };
            } else {
              const label = `${this.$t('message.checkout.payIn', { count: stage.stageNumber })} ${
                res.data.institution === 'DLOCAL' ? `- ${stage.stageName}` : ''
              }`;
              return {
                label,
                value: String(stage.stageNumber), // 确保为字符串
              };
            }
          });

          // 请求成功后标记为不需要刷新
          this.instalmentRefresh = false;

          // 自动选择全额付款逻辑
          this.$nextTick(() => {
            // 检查是否有全额付款选项（stageNumber为'1'）
            if (this.instalmentInfo && this.instalmentInfo.stages && this.instalmentInfo.stages.find(item => item.stageNumber === '1')) {
              // 延迟执行，确保DOM和选项完全更新
              setTimeout(() => {
                // 确认选项中确实有value为'1'的选项
                const fullPayOption = this.installmentOptions.find(option => option.value === '1');
                if (fullPayOption) {
                  this.currentPeriodValue = '1';
                  this.$emit('installment-change', '1');

                  // 强制更新以确保显示正确
                  this.$forceUpdate();
                }
              }, 100);
            }
          });
        })
        .catch(error => {
          console.error('获取分期选项失败:', error);
          this.instalmentInfo = null;
          this.installmentOptions = [];
        })
        .finally(() => {
          this.instalmentLoading = false;
        });
    },
    viewPolicy() {
      this.showPolicy = true;
      this.sendBehaviorEvent({ e: 'click_privacy_policy' });
    },
    onCancel() {
      this.sendBehaviorEvent({ e: 'cancel_interaction' });
      let url = this.order.cancelUrl || this.order.returnUrl;
      if (url) {
        if (url.indexOf('?') > -1) {
          url = `${url}&transactionId=${this.order.txnId}`;
        } else {
          url = `${url}?transactionId=${this.order.txnId}`;
        }
        window.location.href = url;
      }
    },

    // 处理birthDate输入格式化
    birthDateChangeHandler(evt) {
      if (evt.inputType === 'deleteContentBackward') {
        return;
      }

      // 获取当前输入值并清理非法字符
      let value = evt.target.value.replace(/[^\d/]/g, '');

      // 移除所有空格和斜杠，只保留数字
      const numbers = value.replace(/[\s/]/g, '');

      // 自动插入日期分隔符
      if (numbers.length > 0) {
        value = numbers.substring(0, 4);
        if (numbers.length > 4) {
          value += ' / ' + numbers.substring(4, 6);
        }
        if (numbers.length > 6) {
          value += ' / ' + numbers.substring(6, 8);
        }
      }

      // 限制最大长度为14（YYYY / MM / DD：8个数字 + 2个斜杠 + 4个空格 = 14）
      if (value.length > 14) {
        value = value.substring(0, 14);
      }

      // 直接更新输入框的值
      evt.target.value = value;
    },
  },
  // 在组件创建时初始化数据
  created() {
    this.initFormData();
  },
  // 在组件挂载后检查数据
  mounted() {
    if (!this.order || !this.order.paymentConfigurationFields) {
      this.$nextTick(() => {
        this.initFormData();
      });
    }
  },

  // 监听属性变化
  watch: {
    // 监听订单信息变化
    order: {
      handler(newOrder) {
        if (newOrder) {
          // order发生变化时，清空分期选项
          this.installmentOptions = [];
          this.instalmentInfo = null;
          this.currentPeriodValue = '';

          // 只有订单不支持分期时才禁用，否则保持当前状态
          if (!newOrder?.canInstallment) {
            this.instalmentDisabled = true;
            this.lastValidCardNum = '';
          }
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.right-content {
    font-family: Open Sans regular;
    text-align: left;
    width: 100%;
    display: flex;
    flex-direction: column;
    h2 {
        font-family: Open Sans regular;
        font-weight: 600;
        font-size: 18px;
        line-height: 24px;
        letter-spacing: 0px;
        color: rgba(26, 32, 44, 1);
        margin-top: 0;
    }
}

.loading-placeholder {
  position: relative;
  min-height: 100px;
  width: 100%;
  height: auto;

  ::v-deep .el-loading-mask {
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 100;
  }

  ::v-deep .circular {
    width: 32px;
    height: 32px;
  }

  ::v-deep .path {
    stroke: var(--brand-color) !important;
  }
  ::v-deep .el-loading-spinner {
    top: 10% !important;
  }
}
h2.billing-title {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    font-family: Open Sans regular;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0px;
    color: rgba(26, 32, 44, 1);
    justify-content: space-between;
    margin-top: 0 !important;
    margin-bottom: 24px;
}

.arrow-icon {
    width: 14px;
    height: 8px;
    margin-left: 8px;
    transition: transform 0.2s;
    transform: rotate(0deg);
}

.arrow-open {
    transform: rotate(180deg);
}

.pay-btn {
    width: 100%;
    height: 38px;
    gap: 4px;
    border-radius: 8px;
    padding: 8px 24px;
    color: rgba(255, 255, 255, 1);
    margin-bottom: 24px;
    font-family: Open Sans regular;
    font-size: 16px;
}

.pay-agreement {
    color: rgba(141, 152, 175, 1);
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    font-family: Open Sans regular;
    margin-bottom: 24px;
}

.pay-link {
    color: rgba(141, 152, 175, 1);
    cursor: pointer;
    font-family: Open Sans regular;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0px;
    text-align: center;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-offset: 0%;
    text-decoration-thickness: 0%;
}

.pay-link:hover {
  color: #1A202C;
}

.pay-return {
  margin-top: 0px !important;
  text-align: center;
  font-size: 15px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-6px);
}

.return-icon {
  width: 13px;
  height: 12px;
  padding: 0 2px;
  margin-right: 2px;
}

.return-link {
    color: #8D98AF;
    cursor: pointer;
    font-family: Open Sans regular;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0px;
    text-align: center;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-offset: 0%;
    text-decoration-thickness: 0%;
}

.return-link:hover {
    color: #1A202C;
}

/* 折叠动画样式 */
.collapse-enter-active, .collapse-leave-active {
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s;
  overflow: hidden;
}
.collapse-enter, .collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to, .collapse-leave {
  max-height: 1000px;
  opacity: 1;
}

.payment-form-content {
  width: 100%;
}

.country-select-wrapper {
  margin-bottom: 24px;
  width: 100%;
  .country-select-label {
    font-family: Open Sans regular;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #1A202C;
    margin-bottom: 6px;
    font-weight: 500;
  }
}
.subsciption {
  font-family: Open Sans regular;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0px;
  text-align: center;
  color: rgba(26, 32, 44, 1);
  margin-bottom: 24px;
}
.payment-title {
  font-family: Open Sans regular;
font-weight: 600;
font-size: 18px;
line-height: 24px;
letter-spacing: 0px;
color: rgba(26, 32, 44, 1);
margin-bottom: 24px;
}

// 分期字段样式
.installment-field {
  margin-bottom: 24px;
  width: 100%;

  .installment-label {
    font-family: Open Sans regular;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: #1A202C;
    margin-bottom: 6px;
  }

  .installment-select {
    width: 100%;
    height: 38px;
    background: #FFFFFF;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    ::v-deep .el-input__inner {
      height: 38px;
      line-height: 38px;
      border-radius: 8px;
      font-size: 14px;
      border-radius: 8px;
      border: 1px solid #D0D5DD;
      font-size: 16px;
      color: $color-text;
      transition: all 0.3s;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: var(--brand-color);
        box-shadow: 0px 0px 0px 3px #F1F1FF !important;

      }

      &::placeholder {
        color: #8D98AF;
        font-size: 16px;
      }

    }
  }

  .installment-tip {
    font-family: Open Sans regular;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #8D98AF;
    margin-top: 6px;
    margin-bottom: 0;
  }
}

// 加载选项样式
::v-deep .loading-select {
  .select-loading-item {
    text-align: center !important;

    &.el-select-dropdown__item {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 12px 20px !important;
      min-height: 40px !important;

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    }

    .iconfont {
      animation: rotating 2s linear infinite;
      margin-right: 8px;
      display: inline-block;
      font-size: 14px;
    }
  }
}

// 针对Element UI下拉选项的样式优化
::v-deep .el-select-dropdown__item.select-loading-item {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes loading-arrow-spin {
    100% {
        transform: rotate(360deg);
    }
}

.loading-spinner-img {
  width: 14px;
  height: 14px;
  animation: loading-arrow-spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

// 分期加载样式 - 开发者：Nemo，日期：2024-12-28
.installment-loading {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  ::v-deep .el-loading-mask {
    background-color: transparent;
  }

  ::v-deep .circular {
    width: 20px;
    height: 20px;
  }

  ::v-deep .path {
    stroke: var(--brand-color, #503df6) !important;
  }

  ::v-deep .el-loading-spinner {
    position: relative !important;
    top: auto !important;
    left: auto !important;
    margin: 0 !important;
    transform: translateY(5px) !important;
  }
}
</style>
